import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Buildings, Handshake, ShieldCheck, Scales, Percent, CheckCircle } from "@phosphor-icons/react";

// Landing page — public marketing surface, encourages signup / role selection.

const HERO_IMG = "https://images.unsplash.com/photo-1628744448839-a475cc0e90c3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjByZWFsJTIwZXN0YXRlJTIwaG9tZSUyMGV4dGVyaW9yfGVufDB8fHx8MTc4NDI3NjgyOXww&ixlib=rb-4.1.0&q=85";
const APPRAISER_IMG = "https://images.unsplash.com/photo-1503387762-592deb58ef4e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBhcmNoaXRlY3QlMjBhcHByYWlzZXIlMjB3b3JraW5nfGVufDB8fHx8MTc4NDI3NjgyOXww&ixlib=rb-4.1.0&q=85";
const TRUST_IMG = "https://images.unsplash.com/photo-1521791055366-0d553872125f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwzfHxjb250cmFjdCUyMHNpZ25hdHVyZSUyMGhhbmRzfGVufDB8fHx8MTc4NDI3NjgyOXww&ixlib=rb-4.1.0&q=85";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startAuth = () => {
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#F9F8F6] text-zinc-900">
      {/* header */}
      <header className="glass-header">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-[#115E59] text-white">
              <Buildings weight="duotone" size={20} />
            </span>
            <span className="display-serif text-xl">Ledger &amp; Lot</span>
          </div>
          <button onClick={startAuth} className="btn-ink" data-testid="landing-signin-btn">
            Sign in with Google
          </button>
        </div>
      </header>

      {/* hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/55" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-24 lg:py-40 text-white">
          <div className="max-w-3xl">
            <div className="kicker text-white/80 fade-up-1">A fair marketplace for residential appraisals</div>
            <h1 className="display-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02] mt-4 fade-up-2">
              Where lenders, attorneys and homeowners find the right appraiser.
            </h1>
            <p className="mt-6 text-lg text-white/85 max-w-2xl fade-up-3">
              Post a job, review transparent bids from licensed appraisers, and pay securely with escrow.
              Every completed job is peer-reviewed by another appraiser before funds release.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 fade-up-4">
              <button onClick={startAuth} className="btn-ink" data-testid="landing-cta-primary">
                Get started <ArrowRight size={16} />
              </button>
              <a href="#how" className="btn-outline-ink bg-white/10 border-white/30 text-white hover:bg-white/20" data-testid="landing-how-link">
                How it works
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* trust bar */}
      <section className="border-b border-black/5">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { label: "Platform fee", value: "6%", Icon: Percent },
            { label: "Escrow protected", value: "PayPal", Icon: ShieldCheck },
            { label: "Peer reviewed", value: "Every job", Icon: Scales },
            { label: "Roles", value: "Client / Appraiser / Reviewer", Icon: Handshake },
          ].map(({ label, value, Icon }) => (
            <div key={label} className="flex items-start gap-3">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-[#CCFBF1] text-[#115E59]">
                <Icon size={18} weight="duotone" />
              </span>
              <div>
                <div className="kicker">{label}</div>
                <div className="text-base font-medium mt-1">{value}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* how it works */}
      <section id="how" className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-2xl">
            <div className="kicker">How it works</div>
            <h2 className="display-serif text-4xl sm:text-5xl mt-3">A cleaner path from order to payout.</h2>
          </div>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { n: "01", t: "Post a job", d: "Enter the property details, form type and travel distance. AI proposes a fair minimum fee before you post." },
              { n: "02", t: "Receive real bids", d: "Licensed appraisers add complexity fees (unusual lot, remote area, scarce comps) with full transparency. You pick the winner." },
              { n: "03", t: "Escrow via PayPal", d: "Pay the full amount into escrow. Funds are held until the report is peer-reviewed and approved." },
              { n: "04", t: "Report delivered", d: "The winning appraiser uploads the completed report and any supporting files." },
              { n: "05", t: "Peer review", d: "Another appraiser opens the file with an AI-generated QA checklist for their form type and complexity claims." },
              { n: "06", t: "Automatic payout", d: "On approval, PayPal Payouts splits funds — 6% to the platform, reviewer fee, and the remainder to the appraiser." },
            ].map((s, i) => (
              <div key={s.n} className="surface-card p-6 md:p-8 hover:-translate-y-0.5 transition-transform duration-200">
                <div className="kicker text-[#9A3412]">Step {s.n}</div>
                <h3 className="display-serif text-2xl mt-2">{s.t}</h3>
                <p className="text-zinc-600 text-sm mt-2 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* two panels: appraisers + trust */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-6">
          <div className="surface-card overflow-hidden">
            <img src={APPRAISER_IMG} className="h-64 w-full object-cover" alt="" />
            <div className="p-8">
              <div className="kicker">For appraisers</div>
              <h3 className="display-serif text-3xl mt-2">Bid with dignity. Get paid transparently.</h3>
              <p className="text-zinc-600 mt-3">
                See the AI-suggested minimum fee, add complexity from a dropdown, keep the vast majority of the fee.
                Opt into the reviewer pool to earn a second stream of income.
              </p>
              <ul className="mt-4 space-y-2 text-sm">
                {["No AMC middlemen", "Complexity fees you control", "Reviewer earnings on approvals"].map(x => (
                  <li key={x} className="flex items-center gap-2"><CheckCircle size={16} className="text-[#115E59]" weight="fill" /> {x}</li>
                ))}
              </ul>
            </div>
          </div>
          <div className="surface-card overflow-hidden">
            <img src={TRUST_IMG} className="h-64 w-full object-cover" alt="" />
            <div className="p-8">
              <div className="kicker">Escrow &amp; peer review</div>
              <h3 className="display-serif text-3xl mt-2">Trust, engineered.</h3>
              <p className="text-zinc-600 mt-3">
                Every job is peer-reviewed against an AI checklist tuned for the form type (UAD 3.6, 1004, desktop) and the claimed
                complexity. Money only moves on approval.
              </p>
              <ul className="mt-4 space-y-2 text-sm">
                {["PayPal-secured escrow", "USPAP-aligned QA checklists", "Auto-split payouts on approval"].map(x => (
                  <li key={x} className="flex items-center gap-2"><CheckCircle size={16} className="text-[#115E59]" weight="fill" /> {x}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* footer cta */}
      <section className="bg-[#0A0A0A] text-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 text-center">
          <div className="kicker text-white/60">Ready when you are</div>
          <h2 className="display-serif text-4xl sm:text-5xl mt-3 max-w-2xl mx-auto">Order your next appraisal with the pros who deserve the work.</h2>
          <button onClick={startAuth} className="mt-8 inline-flex items-center gap-2 rounded-full bg-white text-black px-6 py-3 text-sm font-medium hover:bg-white/90 transition-colors" data-testid="landing-cta-footer">
            Get started with Google <ArrowRight size={16} />
          </button>
        </div>
      </section>

      <footer className="py-10 text-center text-xs text-zinc-500">
        © {new Date().getFullYear()} Ledger &amp; Lot — Appraisal marketplace.
      </footer>
    </div>
  );
}
